import { useRef, useState } from 'react';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Box, Button, FormHelperText, Paper, Stack, TextField, Typography } from '@mui/material';
import { useInput, useNotify, type InputProps } from 'react-admin';

import {
    ADMIN_BLOG_IMAGE_ACCEPT,
    getAdminBlogAssetRuleSummary,
    resolveAdminBlogAssetUrl,
    uploadAdminBlogAsset,
    validateAdminBlogAssetFile,
} from '../admin/adminBlogDataProvider';

type BlogCoverImageInputProps = InputProps & {
    helperText?: string;
};

export function BlogCoverImageInput(props: BlogCoverImageInputProps) {
    const { field, fieldState, id } = useInput(props);
    const notify = useNotify();
    const uploadInputRef = useRef<HTMLInputElement | null>(null);
    const [isUploading, setIsUploading] = useState(false);

    const currentValue = String(field.value ?? '');
    const resolvedValue = currentValue ? resolveAdminBlogAssetUrl(currentValue) : '';

    const handleUploadClick = () => {
        if (isUploading) {
            return;
        }

        uploadInputRef.current?.click();
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files?.[0];
        event.target.value = '';

        if (!selectedFile) {
            return;
        }

        const validationMessage = validateAdminBlogAssetFile(selectedFile, { imageOnly: true });
        if (validationMessage) {
            notify(validationMessage, { type: 'error' });
            return;
        }

        setIsUploading(true);

        try {
            const uploadResult = await uploadAdminBlogAsset(selectedFile);
            const assetUrl = resolveAdminBlogAssetUrl(uploadResult.fileUrl);
            field.onChange(assetUrl);
            notify('Cover image uploaded', { type: 'info' });
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Cover image upload failed';
            notify(message, { type: 'error' });
        } finally {
            setIsUploading(false);
        }
    };

    const helperMessage =
        fieldState.error?.message ?? props.helperText ?? `You can paste a URL or upload an image. ${getAdminBlogAssetRuleSummary({ imageOnly: true })}`;

    return (
        <Stack spacing={1.5} sx={{ width: '100%' }}>
            <input
                ref={uploadInputRef}
                type="file"
                hidden
                accept={ADMIN_BLOG_IMAGE_ACCEPT}
                onChange={(event) => {
                    void handleFileChange(event);
                }}
            />
            <TextField
                id={id}
                label={props.label ?? 'Cover Image URL'}
                value={currentValue}
                onChange={(event) => field.onChange(event.target.value)}
                onBlur={field.onBlur}
                disabled={props.disabled}
                type="url"
                fullWidth
                error={fieldState.invalid}
                placeholder="https://... or upload an image"
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                <Button
                    variant="outlined"
                    startIcon={<CloudUploadOutlinedIcon />}
                    onClick={handleUploadClick}
                    disabled={Boolean(props.disabled) || isUploading}
                >
                    {isUploading ? 'Uploading...' : 'Upload Cover Image'}
                </Button>
                <Button
                    variant="text"
                    color="inherit"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={() => field.onChange('')}
                    disabled={Boolean(props.disabled) || !currentValue}
                >
                    Clear Cover Image
                </Button>
            </Stack>
            {resolvedValue ? (
                <Paper
                    variant="outlined"
                    sx={{
                        p: 1.5,
                        borderRadius: 4,
                        borderColor: 'rgba(148, 163, 184, 0.28)',
                        background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
                    }}
                >
                    <Stack spacing={1}>
                        <Typography sx={{ color: '#475569', fontSize: '0.84rem', fontWeight: 700 }}>
                            Cover preview
                        </Typography>
                        <Box
                            component="img"
                            src={resolvedValue}
                            alt="Cover preview"
                            sx={{
                                display: 'block',
                                width: '100%',
                                maxWidth: 520,
                                borderRadius: 4,
                                boxShadow: '0 18px 40px rgba(15, 23, 42, 0.16)',
                            }}
                        />
                    </Stack>
                </Paper>
            ) : null}
            <FormHelperText error={fieldState.invalid}>{helperMessage}</FormHelperText>
        </Stack>
    );
}